import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  createSuperAdminLocation,
  getSuperAdminLocation,
  getSuperAdminLocationCountries,
  getSuperAdminLocationTimezones,
  getSuperAdminSettings,
  SuperAdminLocationCountry,
  SuperAdminLocationTimezone,
  updateSuperAdminLocation,
} from "../../services/superAdminApi";

declare global {
  interface Window {
    google?: any;
  }
}

const MAP_SCRIPT_ID = "gmaps-superadmin-locations";

const loadGoogleMaps = (apiKey: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (window.google?.maps) {
      resolve();
      return;
    }
    const existing = document.getElementById(MAP_SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Google Maps failed to load")));
      return;
    }
    const script = document.createElement("script");
    script.id = MAP_SCRIPT_ID;
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Google Maps failed to load"));
    document.body.appendChild(script);
  });
};

const SuperAdminLocationForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const locationId = id ? parseInt(id, 10) : NaN;

  const [name, setName] = useState("");
  const [countryId, setCountryId] = useState("");
  const [timezoneId, setTimezoneId] = useState("");
  const [lat, setLat] = useState("0");
  const [lng, setLng] = useState("0");
  const [countries, setCountries] = useState<SuperAdminLocationCountry[]>([]);
  const [timezones, setTimezones] = useState<SuperAdminLocationTimezone[]>([]);
  const [mapKey, setMapKey] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);

  const syncMarkerFromInputs = useCallback(() => {
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    if (!markerRef.current || Number.isNaN(latNum) || Number.isNaN(lngNum)) return;
    const pos = { lat: latNum, lng: lngNum };
    markerRef.current.setPosition(pos);
    if (mapInstanceRef.current) {
      mapInstanceRef.current.panTo(pos);
    }
  }, [lat, lng]);

  useEffect(() => {
    const boot = async () => {
      try {
        setLoading(true);
        if (isEdit && Number.isNaN(locationId)) {
          setError("Invalid location id.");
          setLoading(false);
          return;
        }
        const [settingsRes, countriesRes] = await Promise.all([
          getSuperAdminSettings(),
          getSuperAdminLocationCountries(),
        ]);
        setMapKey(String(settingsRes?.setting?.map_key || "").trim());
        setCountries(countriesRes || []);

        if (isEdit && !Number.isNaN(locationId)) {
          const loc = await getSuperAdminLocation(locationId);
          setName(loc.name || "");
          setCountryId(String(loc.country_id || ""));
          setTimezoneId(String(loc.timezone_id || ""));
          setLat(loc.lat != null ? String(loc.lat) : "0");
          setLng(loc.lng != null ? String(loc.lng) : "0");
          if (loc.country_id) {
            const tz = await getSuperAdminLocationTimezones(loc.country_id);
            setTimezones(tz || []);
          }
        }
      } catch (e: any) {
        setError(e?.response?.data?.message || "Failed to load form.");
      } finally {
        setLoading(false);
      }
    };
    boot();
  }, [isEdit, locationId]);

  useEffect(() => {
    if (!mapKey || !mapContainerRef.current) return;
    let cancelled = false;

    const setup = async () => {
      try {
        await loadGoogleMaps(mapKey);
        if (cancelled || !mapContainerRef.current || !window.google?.maps) return;

        const latNum = parseFloat(lat) || 26.852594;
        const lngNum = parseFloat(lng) || 26.852594;

        const map = new window.google.maps.Map(mapContainerRef.current, {
          center: { lat: latNum, lng: lngNum },
          zoom: 4,
          mapTypeControl: false,
          streetViewControl: false,
        });
        mapInstanceRef.current = map;

        const marker = new window.google.maps.Marker({
          position: { lat: latNum, lng: lngNum },
          map,
          draggable: true,
        });
        markerRef.current = marker;

        marker.addListener("dragend", () => {
          const p = marker.getPosition();
          if (!p) return;
          setLat(p.lat().toFixed(8));
          setLng(p.lng().toFixed(8));
        });

        map.addListener("click", (e: any) => {
          if (!e.latLng) return;
          marker.setPosition(e.latLng);
          setLat(e.latLng.lat().toFixed(8));
          setLng(e.latLng.lng().toFixed(8));
        });
      } catch {
        // Map optional — manual lat/lng still work
      }
    };

    setup();
    return () => {
      cancelled = true;
      mapInstanceRef.current = null;
      markerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapKey, loading]);

  useEffect(() => {
    if (mapInstanceRef.current && markerRef.current) {
      syncMarkerFromInputs();
    }
  }, [lat, lng, syncMarkerFromInputs]);

  const onCountryChange = async (value: string) => {
    setCountryId(value);
    setTimezoneId("");
    setTimezones([]);
    if (!value) return;
    try {
      const tz = await getSuperAdminLocationTimezones(parseInt(value, 10));
      setTimezones(tz || []);
    } catch {
      setTimezones([]);
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (!name.trim()) {
      setError("Location name is required.");
      return;
    }
    if (!countryId || !timezoneId) {
      setError("Country and timezone are required.");
      return;
    }
    const latN = parseFloat(lat);
    const lngN = parseFloat(lng);
    if (Number.isNaN(latN) || Number.isNaN(lngN)) {
      setError("Valid latitude and longitude are required.");
      return;
    }

    const payload = {
      name: name.trim(),
      country_id: parseInt(countryId, 10),
      timezone_id: parseInt(timezoneId, 10),
      lat: latN,
      lng: lngN,
    };

    try {
      setSaving(true);
      if (isEdit && !Number.isNaN(locationId)) {
        await updateSuperAdminLocation(locationId, payload);
      } else {
        await createSuperAdminLocation(payload);
      }
      navigate("/superadmin/locations");
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      const errs = e?.response?.data?.errors;
      if (errs && typeof errs === "object") {
        setError(Object.values(errs).flat().join(" ") || msg || "Validation failed.");
      } else {
        setError(msg || "Could not save location.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-600">Loading...</div>;
  }

  const title = isEdit ? "Edit Location" : "Add Location";

  return (
    <div className="space-y-5 p-1 max-w-5xl">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#402B69]">{title}</h1>
          <p className="text-sm text-gray-500">Set name, country, coordinates and timezone.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/superadmin/locations")}
          className="text-sm font-semibold text-[#402B69] hover:underline"
        >
          Back to list
        </button>
      </div>

      {error ? <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2">{error}</div> : null}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gray-50 border-b px-4 py-3">
          <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Location name <span className="text-red-500">*</span>
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Location country <span className="text-red-500">*</span>
              </label>
              <select
                value={countryId}
                onChange={(e) => onCountryChange(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select location</option>
                {countries.map((c) => (
                  <option key={c.id} value={c.id}>
                    +{c.phonecode} - {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Latitude <span className="text-red-500">*</span>
              </label>
              <input
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Longitude <span className="text-red-500">*</span>
              </label>
              <input
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
                autoComplete="off"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">
                Location timezone <span className="text-red-500">*</span>
              </label>
              <select
                value={timezoneId}
                onChange={(e) => setTimezoneId(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">Select timezone</option>
                {timezones.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.zone_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-2">Company location (map)</p>
            <div
              ref={mapContainerRef}
              className="w-full rounded-lg border border-gray-200 bg-gray-100"
              style={{ height: 400 }}
            />
            <p className="text-xs text-red-600 mt-2">
              {mapKey
                ? "Pick a point on the map or drag the marker; latitude and longitude update automatically."
                : "Configure Google Maps API key in Superadmin Settings to enable the map picker. You can still enter latitude and longitude manually."}
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              disabled={saving}
              onClick={handleSubmit}
              className="bg-[#885ABB] hover:bg-[#7748A4] text-white px-5 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => navigate("/superadmin/locations")}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-5 py-2 rounded-lg text-sm font-semibold"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminLocationForm;
